import { QuotePdfDialog, Quote } from '@paintscout/react-quote'
import { QuoteLinkDialog } from 'src/components/dialogs'
import type { Theme } from '@material-ui/core/styles'
import type { DialogProps, StyleClasses } from '@paintscout/ui'
import ClearIcon from '@material-ui/icons/Clear'
import type { QuoteDocument } from 'paintscout'
import { makeStyles } from '@material-ui/core'
import { goToQuote } from 'src/util/routing'
import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  useDialogs,
  useClientOptions,
  LinkIcon,
  DownloadIcon
} from '@paintscout/ui'

const useStyles = makeStyles<Theme, WorkOrderViewDialogDialogProps>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    dialogTitle: {},
    dialogContent: {
      maxWidth: '100%',
      overflowX: 'hidden'
    },
    closeIcons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1)
    },
    cancel: {},
    confirm: {}
  }),
  { name: 'WorkOrderViewDialogDialog' }
)

export interface WorkOrderViewDialogDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  quote: QuoteDocument
  scrollToBottom?: boolean
  onCancel: () => any
}

function WorkOrderViewDialog(props: WorkOrderViewDialogDialogProps) {
  const { onCancel, quote, scrollToBottom, ...baseDialogProps } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { options, clientId } = useClientOptions()
  const classes = useStyles(props)

  useEffect(() => {
    if (scrollToBottom) {
      const timer = setTimeout(() => {
        const dialogContent = document.getElementById('work-order-dialog-content')
        if (dialogContent) {
          dialogContent.scrollTo({ top: dialogContent.scrollHeight, behavior: 'smooth' })
        }
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [scrollToBottom])

  const rightContent = (
    <div className={classes.closeIcons}>
      <IconButton
        color="inherit"
        aria-label="Close"
        onClick={() =>
          goToQuote({
            id: quote._id,
            invoice: true,
            params: {
              view: 'work-order'
            }
          })
        }
      >
        <Typography variant="body2">Edit</Typography>
      </IconButton>
      <IconButton color="inherit" aria-label="Link" onClick={handleGetLink}>
        <LinkIcon />
      </IconButton>
      <IconButton color="inherit" aria-label="Download" onClick={handleDownloadPDF}>
        <DownloadIcon />
      </IconButton>
      <IconButton color="inherit" aria-label="Close" onClick={onCancel}>
        <ClearIcon />
      </IconButton>
    </div>
  )

  function handleGetLink() {
    openDialog(QuoteLinkDialog, {
      view: 'work-order',
      onConfirm: dismissDialog,
      quote
    })
  }

  function handleDownloadPDF() {
    openDialog(QuotePdfDialog, {
      quote,
      companyId: clientId,
      options,
      silent: true,
      view: 'work-order',
      onConfirm: dismissDialog
    })
  }

  return (
    <Dialog fullWidth={true} maxWidth="lg" {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2" weight={'normal'}>
          Work Order
        </Typography>
      </DialogTitle>
      <DialogContent id={'work-order-dialog-content'} classes={{ root: classes.dialogContent }}>
        <Quote quote={quote} view={'work-order'} />
      </DialogContent>
    </Dialog>
  )
}

export default WorkOrderViewDialog
